import React from 'react';
import { Alert, Table } from 'reactstrap';
import Counter from '@/components/elements/Counter';
import { BookingStateType, PricesStateType, PriceType, ServicesStateType, ServiceType } from '@/types';
import { useSelector, useDispatch } from 'react-redux';
import { FormGroup, Label, Input } from 'reactstrap';
import Panel from '@/components/elements/Panel';
import { Trans } from '@lingui/macro';
import { withI18n } from '@lingui/react';
import { I18n } from '@lingui/core';

import { format } from '@/misc/datetime';
import {
    hasGroupBooking as isGroupBooking,
    hasMultipleResources as isMultipleResource,
} from '@/misc/serviceHelpers';
import { hasFlexibleTime } from './Service';
import Price from './PriceCell';

const calculateHours = (fromTime: string, toTime: string) => {
    const start = new Date(fromTime);
    const end = new Date(toTime);
    const diffHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    return diffHours;
}


const getNaturalFromTo = (from: string, to: string, i18n: I18n) => {
    if (!from) return '';

    const iStoTimeInfinite = to.includes('9999');
    const isWholeDay = (from.includes('00:00:00') && to.includes('23:59:00')) || (from.includes('00:00:00') && to.includes('23:59:59'))

    let text = `${i18n._('selectQuantities.availableFrom')}`;

    if (!iStoTimeInfinite) {
        text = `${text} ${format(from, "PPp")}-${format(to, "PPp")}`
    } else if (!isWholeDay) {
        text = `${text} ${format(from, "p")} - ${format(to, "p")}`
    }

    return text;
}

export const SelectQuantities: React.FC<{ i18n: I18n }> = (props) => {
    const { i18n } = props;
    const dispatch = useDispatch();
    const { booking, services, prices: pricesState } = useSelector(({ booking, services, prices, ...rest }: {
        booking: BookingStateType,
        services: any,
        prices: PricesStateType
    }) => {
        return {
            services: services.data,
            booking,
            prices
        };
    });


    const { finalService: service, quantities, time } = booking;
    const allPricesFromService: PriceType[] = services.find((s: ServiceType) => s.Id === service?.Id)?.Prices
        ? services.find((s: ServiceType) => s.Id === service?.Id)?.Prices
        : []

    const hours = time ? calculateHours(time.From, time.To) : 1;

    const flexibleExecutionTime = service && hasFlexibleTime(service);

    const priceMultiplier = flexibleExecutionTime ? hours : 1;


    if (!service) return null;

    const prices = service.Prices;
    const hasPrices = prices.length > 0;
    const hasMultiplePrices = service.Prices && service.Prices.length > 1;

    const maxQuantity = service.GroupBooking.Active
        ? service.GroupBooking.Max
        : service.MultipleResource.Max;

    // const totalPrice = quantities
    //     .map((quantity) => (quantity.Price || 0) * quantity.Quantity * priceMultiplier)
    //     .reduce((a, b) => a + b, 0);

    const totalQuantity = quantities
        .map((quantity) => quantity.Quantity)
        .reduce((a, b) => a + b, 0);

    const spotsLeft = booking.time?.Free || 0;

    const hasGroupBooking = isGroupBooking(service);

    const hasMultipleResource = isMultipleResource(service);

    if (!(hasGroupBooking || hasMultipleResource) && !hasMultiplePrices) return null;

    if (!(hasGroupBooking || hasMultipleResource) && hasMultiplePrices) {
        const quantity = booking.quantities[0];

        return (
            <>
                <Panel>
                    {prices.map((price: PriceType) => (
                        <FormGroup
                            key={price.Id}
                            check
                            data-testid={`select-price-quantity-${price.Id}`}
                        >
                            <Label check>
                                <Input
                                    type="radio"
                                    name="Price"
                                    checked={quantity ? quantity.PriceId === price.Id : false}
                                    onChange={(event) => {
                                        dispatch({
                                            type: 'SELECT_PRICE',
                                            price: price,
                                        });
                                    }}
                                />{' '}
                                {price.PriceText} {!flexibleExecutionTime && priceMultiplier > 1 ? `(${price.Price * priceMultiplier}${price.PriceSign})` : ''}
                            </Label>
                        </FormGroup>
                    ))}
                    {allPricesFromService
                        .filter(price => !prices.map(p => p.Id).includes(price.Id))
                        .map((price) => {
                            const { Id, From, FromTime, To, ToTime } = price;
                            const fromTime = `${From.split('T')[0]}T${FromTime}`;
                            const toTime = `${To.split('T')[0]}T${ToTime}`;

                            return (
                                <FormGroup
                                    key={Id}
                                    check
                                    title={getNaturalFromTo(fromTime, toTime, i18n)}
                                    data-testid={`select-price-quantity-${Id}`}
                                >
                                    <Label check style={{ color: '#ccc' }}>
                                        <Input
                                            disabled
                                            type="radio"
                                            name="Price"
                                            checked={quantity ? quantity.PriceId === Id : false}
                                            onChange={(event) => {
                                                dispatch({
                                                    type: 'SELECT_PRICE',
                                                    price: price,
                                                });
                                            }}
                                        />{' '}
                                        {price.PriceText} {!flexibleExecutionTime && priceMultiplier > 1 ? `(${price.Price * priceMultiplier}${price.PriceSign})` : ''}
                                    </Label>
                                </FormGroup>
                            )
                        })}
                </Panel>
                <Table size="sm" responsive borderless>
                    <tfoot>
                        <tr>
                            <td className="text-left">{<Trans id="totally"></Trans>}</td>
                            {pricesState.data?.TotalPrice !== undefined && (
                                <td data-testid="SelectQuantities__totalPrice">
                                    {pricesState.data.TotalPrice >= 0 ? pricesState.data.TotalPrice : 0}
                                    {pricesState.data.PriceSign}
                                </td>
                            )}
                        </tr>
                    </tfoot>
                </Table>
            </>
        );
    }

    return (
        <>
            <Table size="sm" responsive borderless>
                <thead>
                    <tr>
                        {hasPrices && <th />}
                        {hasPrices && (hasGroupBooking || hasMultipleResource) && (
                            <th className="text-left">{<Trans id="price"></Trans>}</th>
                        )}
                        {(hasGroupBooking || hasMultipleResource) && (
                            <th className="text-center">{<Trans id="number"></Trans>}</th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {prices.map((price, idx) => {
                        const currentQuantity = quantities.find(
                            (quantity) => quantity.PriceId === price.Id
                        );

                        const quantity = currentQuantity ? currentQuantity.Quantity : 0;

                        return (
                            // @ts-ignore'
                            <tr key={idx}>
                                <td className="text-left">
                                    {!(hasGroupBooking || hasMultipleResource) &&
                                        hasMultiplePrices && <input type="radio" />}
                                    {price.Category || <Trans id="price"></Trans>}
                                </td>

                                <td className="text-left">
                                    <Price
                                        hasGroupBooking={hasGroupBooking}
                                        hasMultipleResource={hasMultipleResource}
                                        hasMultiplePrices={hasMultiplePrices}
                                        pricesState={pricesState}
                                        price={price}
                                    />
                                </td>
                                {(hasGroupBooking || hasMultipleResource) && (
                                    <td className="text-center">
                                        <Counter
                                            value={quantity}
                                            onDecrement={() =>
                                                dispatch({
                                                    type: 'CHANGE_QUANTITY',
                                                    value: quantity - 1,
                                                    price,
                                                })
                                            }
                                            onIncrement={() =>
                                                dispatch({
                                                    type: 'CHANGE_QUANTITY',
                                                    value: quantity + 1,
                                                    price,
                                                })
                                            }
                                        />
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                    {!hasPrices && (
                        <tr>
                            <td>
                                <Counter
                                    value={totalQuantity}
                                    onDecrement={() =>
                                        dispatch({
                                            type: 'CHANGE_QUANTITY',
                                            value: totalQuantity - 1,
                                        })
                                    }
                                    onIncrement={() =>
                                        dispatch({
                                            type: 'CHANGE_QUANTITY',
                                            value: totalQuantity + 1,
                                        })
                                    }
                                />
                            </td>
                        </tr>
                    )}
                </tbody>
                <tfoot>
                    {(((hasGroupBooking || hasMultipleResource) && hasPrices) ||
                        hasMultiplePrices) && (
                            <tr>
                                <td className="text-left">{<Trans id="totally"></Trans>}</td>
                                {pricesState.data?.TotalPrice && (
                                    <td data-testid="SelectQuantities__totalPrice">
                                        {pricesState.data.TotalPrice >= 0 ? pricesState.data.TotalPrice : 0}
                                        {pricesState.data.PriceSign}
                                    </td>
                                )}
                                {(hasGroupBooking || hasMultipleResource) && (
                                    <td className="text-center">{totalQuantity}</td>
                                )}
                            </tr>
                        )}
                </tfoot>
            </Table>
            {((totalQuantity > maxQuantity) || (!booking.service?.EnableBookingQueue && totalQuantity > spotsLeft)) && (
                <Alert color="danger">{<Trans id="quantityLimitExceeded"></Trans>}</Alert>
            )}
        </>
    );
};

export default withI18n()(SelectQuantities);
